import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Chip } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';

import SidePanel from "@component/SidePanel"
import { useChallengesQuery } from "@query/challengeQuery";

import { useTranslation } from "react-i18next";

import { formatDateTimeWithoutSeconds } from "@utils/timeUtil";
import AddIcon from '@mui/icons-material/Add';
import ChallengeForm from "@component/ChallengeForm";
import ChallengeCreateForm from "@component/ChallengeCreateForm";

export default function ChallengePage() {
  const [challenges, setChallenges] = useState(null);
  const { t } = useTranslation(["main", "challenges"]);

  const [challengeDetailOpen, setChallengeDetailOpen] = useState(false);
  const [selectedItemUuid, setSelectedItemUuid] = useState(null);
  const selectedItem = challenges?.items?.find(item => item.uuid === selectedItemUuid);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10
  });

  const challengesQuery = useChallengesQuery(paginationModel.page);

  function handleOnChallengeDetailClick() {
    setChallengeDetailOpen(false)
    challengesQuery.refetch()
  }

  useEffect(() => {
    if (challengesQuery.data?.data) {
      setChallenges(challengesQuery.data.data);
    }
  }, [challengesQuery.data]);

  const handleRowClick = (uuid) => {
    setSelectedItemUuid(uuid);
    setChallengeDetailOpen(true);
  };

  const handleNewChallengeClick = () => {
    setSelectedItemUuid(null);
    setChallengeDetailOpen(true);
  };

  const handleOnCreateClose = () => {
    setSelectedItemUuid(null);
    setChallengeDetailOpen(false);
  };

  const handleSave = () => {
    challengesQuery.refetch();
  };

  const handleOnCreateSave = () => {
    handleSave();
    setSelectedItemUuid(null);
    setChallengeDetailOpen(false);
  };

  const getStatusChip = (challenge) => {
    if (!challenge) return null;

    const now = new Date();
    const startDate = new Date(challenge.start);
    const endDate = new Date(challenge.end);

    if (now >= startDate && now <= endDate) {
      return (
        <Chip
          label={t('challenges:statusActive')}
          color="success"
          size="small"
          onClick={() => { }}
        />
      );
    }

    if (now < startDate) {
      return (
        <Chip
          label={t('challenges:statusNotStarted')}
          color="info"
          size="small"
        />
      );
    }

    return (
      <Chip
        label={t('challenges:statusNotActive')}
        color="error"
        size="small"
      />
    );
  };

  const columns = [
    {
      field: "name",
      headerName: t('challenges:name'),
      sortable: false,
      filterable: false,
      flex: 1,
      minWidth: 250,
      cellClassName: 'column-heading'
    },
    {
      field: 'status',
      headerName: t('challenges:status'),
      sortable: false,
      filterable: false,
      width: 150,
      renderCell: (params) => getStatusChip(params.row),
    },
    {
      field: 'hosting',
      headerName: t('challenges:hosting'),
      sortable: false,
      filterable: false,
      width: 300,
      valueGetter: (params, row) => {
        if (row.hosting_site) return t('main:site') + " - " + row.hosting_site.name;
        else if (row.hosting_club) return t('main:club') + " - " + row.hosting_club.name;
        return "";
      },
    },
    {
      field: "start",
      headerName: t('challenges:start'),
      sortable: false,
      filterable: false,
      width: 200,
      valueFormatter: (params) => {
        if (params) {
          return formatDateTimeWithoutSeconds(params);
        }
        return '';
      },
    },
    {
      field: "end",
      headerName: t('challenges:end'),
      sortable: false,
      filterable: false,
      width: 200,
      valueFormatter: (params) => {
        if (params) {
          return formatDateTimeWithoutSeconds(params);
        }
        return '';
      },
    },
  ];

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: "95%", md: "95%" },
        display: "flex",
        flexDirection: "column",
        gap: 3,
        mx: "auto",
        pb: 4,
        pt: 4
      }}
    >
      <SidePanel
        open={challengeDetailOpen}
        onClose={handleOnChallengeDetailClick}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {t('challenges:challenge')}
            {selectedItemUuid && getStatusChip(selectedItem)}
          </Box>
        }
      >
        {selectedItemUuid ?
          <ChallengeForm uuid={selectedItemUuid} onSave={handleSave} /> :
          <ChallengeCreateForm onSave={handleOnCreateSave} onCancel={handleOnCreateClose} />
        }
      </SidePanel>

      <Box sx={{
        flex: 1,
        height: 400,
        minWidth: 0
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="h2" mb={1}>
              {t('challenges:challenges')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('challenges:pageSubtitle')}
            </Typography>
          </Box>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => handleNewChallengeClick()}
            sx={{ mb: 2 }}
          >
            {t('challenges:createChallenge')}
          </Button>
        </Box>
        <DataGrid
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer'
            },
            '& .column-heading': {
              fontWeight: 'bold'
            }
          }}
          paginationMode="server"
          rowCount={challenges?.pagination?.total_items || 0}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rows={challenges?.items.map(challenge => ({ ...challenge, id: challenge.uuid }))}
          loading={challengesQuery.isFetching}
          columns={columns}
          onRowClick={(params) => handleRowClick(params.id)}
          disableRowSelectionOnClick
          hideFooterSelectedRowCount={true}
          pageSizeOptions={[10]}
        />
      </Box>
    </Box>
  );
}